import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import BundeslaenderAvailability from "../components/BundeslaenderAvailability";
import QuickReportHero from "../components/QuickReportHero";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

if (L.Icon.Default.prototype._getIconUrl) {
  delete L.Icon.Default.prototype._getIconUrl;

  L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
  });
}

export default function QuickReportPage() {
  useEffect(() => {
    document.title =
      "GeoReport: Erdwärme-Grundstück online prüfen | Erdwärme-Experten";

    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Prüfen Sie, ob Erdwärme auf Ihrem Grundstück sinnvoll ist. Datenbasierter GeoReport zu Untergrund, Systemwahl, Kosten und Förderung."
    );

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.erdwaerme-experten.de/report";
  }, []);

  const API_BASE =
    window.location.hostname === "localhost"
      ? "http://127.0.0.1:8000"
      : "https://geotherm-app-backend.onrender.com";

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState([52.52, 13.405]);
  const [geo, setGeo] = useState(null);
  const [consentToWaiver, setConsentToWaiver] = useState(false);
  const [stateMatch, setStateMatch] = useState(null); // null = ungeprüft, true = passt, false = passt nicht
  const [validationErrors, setValidationErrors] = useState([]);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const [form, setForm] = useState({
    bundesland: "Berlin",
    gebaeudetyp: "Neubau",
    wohnflaeche: null,
    heizwaermebedarf: null,
    baujahr: null,
    daemmstandard: "EnEV 2009",
    heizsystem: "Fußbodenheizung (niedrig)",
    warmwasserbedarf: "Über Heizung/Wärmepumpe",
    personen: null,
    grundstuecksgroesse: null,
    grundwassertiefe: null,
    bodenart: "Unbekannt",
    self_occupied: false,
    taxable_household_income: null,
    has_eligible_child: false,
    speed_bonus_eligible: false,
    //paket: "Standard",
	paket: "Beta",
  });
  
  const isBeta = true; // später dynamisch

  const STATE_MAP = {
    "Baden-Württemberg": ["Baden-Württemberg"],
    Bayern: ["Bayern", "Bavaria"],
    Berlin: ["Berlin"],
    Brandenburg: ["Brandenburg"],
    Bremen: ["Bremen"],
    Hamburg: ["Hamburg"],
    Hessen: ["Hessen"],
    "Mecklenburg-Vorpommern": ["Mecklenburg-Vorpommern"],
    Niedersachsen: ["Niedersachsen", "Lower Saxony"],
    NRW: ["Nordrhein-Westfalen", "North Rhine-Westphalia"],
    "Rheinland-Pfalz": ["Rheinland-Pfalz"],
    Saarland: ["Saarland"],
    Sachsen: ["Sachsen", "Saxony"],
    "Sachsen-Anhalt": ["Sachsen-Anhalt"],
    "Schleswig-Holstein": ["Schleswig-Holstein"],
    Thüringen: ["Thüringen", "Thuringia"],
  };

  function RecenterMap({ coords }) {
    const map = useMap();
    map.setView(coords, 13);
    return null;
  }

  function validateForm(includeConsent = false) {
    const errors = [];

    if (!address.trim()) {
      errors.push("Bitte geben Sie eine Adresse ein.");
    }

    if (!geo) {
      errors.push(
        "Bitte suchen und bestätigen Sie zuerst eine gültige Adresse."
      );
    }

    if (stateMatch === false) {
      errors.push(
        "Die eingegebene Adresse passt nicht zum ausgewählten Bundesland."
      );
    }

    if (form.wohnflaeche == null || form.wohnflaeche <= 0) {
      errors.push("Bitte geben Sie die Wohnfläche an.");
    }

    if (
      form.gebaeudetyp === "Bestand (Sanierung)" &&
      (form.baujahr == null || form.baujahr <= 0)
    ) {
      errors.push(
        "Bitte geben Sie für ein Bestandsgebäude das Baujahr an."
      );
    }

    if (
      form.grundstuecksgroesse == null ||
      form.grundstuecksgroesse <= 0
    ) {
      errors.push("Bitte geben Sie die unbebaute Grundstücksgröße an.");
    }

    if (
      form.taxable_household_income != null &&
      form.taxable_household_income < 0
    ) {
      errors.push(
        "Das zu versteuernde Haushaltseinkommen darf nicht negativ sein."
      );
    }

    if (includeConsent && !consentToWaiver) {
      errors.push(
        "Bitte bestätigen Sie den Beginn der Ausführung vor Ablauf der Widerrufsfrist."
      );
    }

    return errors;
  }

  const liveValidationErrors = useMemo(
    () => validateForm(false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [address, geo, stateMatch, form, consentToWaiver]
  );

  const canCheckout = liveValidationErrors.length === 0 && consentToWaiver;

  async function handleGeocode() {
    if (!address.trim()) {
      setValidationErrors(["Bitte geben Sie eine Adresse ein."]);
      alert("Bitte geben Sie eine Adresse ein.");
      return;
    }

    setIsGeocoding(true);
    setValidationErrors([]);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(
          address
        )}`
      );
      const data = await res.json();

      if (!data.length) {
        setGeo(null);
        setStateMatch(null);
        setValidationErrors(["Adresse nicht gefunden."]);
        alert("Adresse nicht gefunden.");
        return;
      }

      const latNum = parseFloat(data[0].lat);
      const lonNum = parseFloat(data[0].lon);

      const rev = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latNum}&lon=${lonNum}&addressdetails=1`
      );
      const revData = await rev.json();

      const addr = revData?.address || {};

      const foundState =
        addr.state ||
        addr.state_district ||
        addr.region ||
        (addr.city && ["Berlin", "Hamburg", "Bremen"].includes(addr.city)
          ? addr.city
          : null);

      if (!foundState) {
        console.warn(
          "Bundesland nicht eindeutig ermittelbar, fahre fort:",
          addr
        );
        setCoords([latNum, lonNum]);
        setGeo({ lat: latNum, lon: lonNum });
        setStateMatch(null);
        return;
      }

      const selectedState = form.bundesland;
      const validNames = STATE_MAP[selectedState] || [];

      const isMatch = validNames.some((s) =>
        foundState.toLowerCase().includes(s.toLowerCase())
      );

      if (!isMatch) {
        setGeo(null);
        setStateMatch(false);
        setValidationErrors([
          `Die eingegebene Adresse liegt in „${foundState}“ und nicht im ausgewählten Bundesland „${selectedState}“.`,
        ]);
        alert(
          `⚠️ Die eingegebene Adresse liegt in „${foundState}“, nicht im ausgewählten Bundesland „${selectedState}“.`
        );
        return;
      }

      setCoords([latNum, lonNum]);
      setGeo({ lat: latNum, lon: lonNum });
      setStateMatch(true);
    } catch (err) {
      console.error(err);
      setGeo(null);
      setStateMatch(null);
      setValidationErrors([
        "Die Adresse konnte derzeit nicht verarbeitet werden.",
      ]);
      alert("Die Adresse konnte derzeit nicht verarbeitet werden.");
    } finally {
      setIsGeocoding(false);
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const optionalFloats = [
      "grundwassertiefe",
      "wohnflaeche",
      "grundstuecksgroesse",
      "taxable_household_income",
    ];

    const optionalInts = ["personen", "baujahr", "heizwaermebedarf"];

    let newValue;
    if (type === "checkbox") {
      newValue = checked;
    } else if (optionalFloats.includes(name)) {
      newValue = value === "" ? null : parseFloat(value);
    } else if (optionalInts.includes(name)) {
      newValue = value === "" ? null : parseInt(value, 10);
    } else {
      newValue = value;
    }

    setForm((prev) => {
      const next = { ...prev, [name]: newValue };

      if (name === "self_occupied" && !newValue) {
        next.taxable_household_income = null;
        next.has_eligible_child = false;
        next.speed_bonus_eligible = false;
      }

      if (name === "gebaeudetyp" && newValue !== "Bestand (Sanierung)") {
        next.self_occupied = false;
        next.taxable_household_income = null;
        next.has_eligible_child = false;
        next.speed_bonus_eligible = false;
      }

      return next;
    });

    if (name === "bundesland") {
      setGeo(null);
      setStateMatch(null);
    }
  };

  async function handleCheckout() {
    const errors = validateForm(true);
    setValidationErrors(errors);

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    const payload = {
      address,
      lat: geo.lat,
      lon: geo.lon,
      ...form,
      baujahr: form.baujahr ?? 2025,
      consent_to_waiver: true,
    };

    try {
      const res = await fetch(`${API_BASE}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        alert("Fehler: " + data.detail);
        return;
      }

      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      alert("Fehler beim Verbinden mit Stripe.");
    }
  }

  async function handleLocalPDF() {
    const errors = validateForm(false);
    setValidationErrors(errors);

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    const payload = {
      address,
      lat: geo.lat,
      lon: geo.lon,
      ...form,
      baujahr: form.baujahr ?? 2025,
    };

    const testInvoiceNo = "TEST-LOCAL-001";

    try {
      const res = await fetch(
        `${API_BASE}/quick-report/pdf?invoice_no=${encodeURIComponent(
          testInvoiceNo
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        console.error("Backend-Fehler:", err);
        alert("Fehler:\n" + JSON.stringify(err.detail, null, 2));
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "QuickReport_TEST.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      alert("PDF-Test fehlgeschlagen.");
    }
  }

  const fieldClassName =
    "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-slate-900 shadow-sm outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-100";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-[#f6f3f2] pb-20 pt-14 sm:pt-20">
      <div>
        <QuickReportHero />

        {/* Adresseingabe */}
        <div className="mx-4 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
          <p className="mb-2 text-sm font-semibold text-red-700">
            Schritt 1
          </p>
          <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Standort auswählen
          </h2>
          <p className="mt-3 max-w-3xl leading-7 text-slate-600">
            Geben Sie zunächst die Adresse Ihres Grundstücks ein und wählen Sie
            das zugehörige Bundesland. Nach der Bestätigung ergänzen Sie die
            Gebäude- und Nutzungsdaten.
          </p>

          <div className="mt-7 flex flex-col items-stretch gap-3 md:flex-row">
            <input
              type="text"
              placeholder="Adresse eingeben..."
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                setGeo(null);
                setStateMatch(null);
              }}
              className={`${fieldClassName} min-w-[250px] flex-1`}
            />

            <select
              name="bundesland"
              value={form.bundesland}
              onChange={handleChange}
              className={`${fieldClassName} md:w-56`}
            >
              <option>Baden-Württemberg</option>
              <option>Bayern</option>
              <option>Berlin</option>
              <option>Brandenburg</option>
              <option>Bremen</option>
              <option>Hamburg</option>
              <option>Hessen</option>
              <option>Mecklenburg-Vorpommern</option>
              <option>Niedersachsen</option>
              <option>NRW</option>
              <option>Rheinland-Pfalz</option>
              <option>Saarland</option>
              <option>Sachsen</option>
              <option>Sachsen-Anhalt</option>
              <option>Schleswig-Holstein</option>
              <option>Thüringen</option>
            </select>

            <button
              onClick={handleGeocode}
              disabled={isGeocoding}
              className={`w-full rounded-xl px-6 py-3 font-semibold shadow-sm transition md:w-auto ${
                isGeocoding
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {isGeocoding ? "Suche läuft..." : "Adresse suchen"}
            </button>
          </div>

          <div className="mt-3">
            <BundeslaenderAvailability />
          </div>

          {stateMatch === true && geo && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm">
              Die Adresse wurde erfolgreich gefunden und passt zum ausgewählten
              Bundesland.
            </div>
          )}

          {stateMatch === false && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
              Die eingegebene Adresse passt nicht zum ausgewählten Bundesland.
              Bitte korrigieren Sie entweder die Adresse oder die Auswahl des
              Bundeslandes.
            </div>
          )}
        </div>

        {/* Karte */}
        <div className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-2 shadow-sm sm:mx-6 sm:p-3 xl:mx-auto">
          <div className="relative z-0 overflow-hidden rounded-2xl border border-slate-200">
            <MapContainer
              center={coords}
              zoom={13}
              style={{ height: "45vh", width: "100%" }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={coords} />
              <RecenterMap coords={coords} />
            </MapContainer>
          </div>
        </div>

        {/* Formular */}
        <div className="mx-4 mt-10 max-w-6xl sm:mx-6 xl:mx-auto">
          <div className="mb-6 px-2 sm:px-0">
            <p className="mb-2 text-sm font-semibold text-red-700">Schritt 2</p>
            <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
              Gebäude- und Standortdaten
            </h2>
          </div>

          <form
            className="grid grid-cols-1 gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 md:grid-cols-2 lg:p-10"
            onSubmit={(e) => e.preventDefault()}
          >
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Gebäudetyp</label>
              <select
                name="gebaeudetyp"
                value={form.gebaeudetyp}
                onChange={handleChange}
                className={fieldClassName}
              >
                <option>Neubau</option>
                <option>Bestand (Sanierung)</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Wohnfläche (m²) <span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="wohnflaeche"
                value={form.wohnflaeche ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Jährlicher Heizwärmebedarf (kWh/a) – optional
              </label>
              <input
                type="number"
                name="heizwaermebedarf"
                placeholder="z. B. 12.000"
                value={form.heizwaermebedarf ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
              <p className="text-sm text-slate-500 mt-1">
                Falls bekannt, z. B. aus Energieausweis oder
                Heizkostenabrechnung. Überschreibt die automatische Berechnung.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Baujahr (falls Bestand)
                {form.gebaeudetyp === "Bestand (Sanierung)" && (
                  <span className="text-red-600"> *</span>
                )}
              </label>
              <input
                type="number"
                name="baujahr"
                value={form.baujahr ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Dämmstandard</label>
              <select
                name="daemmstandard"
                value={form.daemmstandard}
                onChange={handleChange}
                className={fieldClassName}
              >
                <option>Unsaniert</option>
                <option>Teilsaniert</option>
                <option>EnEV 2009</option>
                <option>KfW-55</option>
                <option>Passivhaus</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Heizsystem</label>
              <select
                name="heizsystem"
                value={form.heizsystem}
                onChange={handleChange}
                className={fieldClassName}
              >
                <option>Fußbodenheizung (niedrig)</option>
                <option>Radiatoren (mittel)</option>
                <option>Hochtemperatur-System</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Warmwasserbedarf</label>
              <select
                name="warmwasserbedarf"
                value={form.warmwasserbedarf}
                onChange={handleChange}
                className={fieldClassName}
              >
                <option>Über Heizung/Wärmepumpe</option>
                <option>Dezentrale Warmwasserbereitung</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Personen im Haushalt (optional)
              </label>
              <input
                type="number"
                name="personen"
                value={form.personen ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Unbebaute Grundstücksgröße (m²){" "}
                <span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="grundstuecksgroesse"
                value={form.grundstuecksgroesse ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Grundwassertiefe (m)
              </label>
              <input
                type="number"
                name="grundwassertiefe"
                step="0.1"
                placeholder="z. B. 1,2"
                value={form.grundwassertiefe ?? ""}
                onChange={handleChange}
                className={fieldClassName}
              />
              <p className="text-sm text-slate-500 mt-1">
                Falls bekannt oder aus Gutachten.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Bodenart (falls bekannt)
              </label>
              <select
                name="bodenart"
                value={form.bodenart}
                onChange={handleChange}
                className={fieldClassName}
              >
                <option>Sandig</option>
                <option>Lehmig</option>
                <option>Tonige Böden</option>
                <option>Kies/Schotter</option>
                <option>Unbekannt</option>
              </select>
            </div>

            <div className="md:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 sm:p-6">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold text-emerald-800">
                  Förderangaben (optional)
                </p>
                <h3 className="mt-1 text-lg font-bold text-slate-950">
                  Persönliche Boni realistisch einordnen
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Die Grundförderung wird bei einem förderfähigen Bestandsgebäude
                  unabhängig davon angesetzt. Die folgenden Angaben werden nur
                  verwendet, um mögliche persönliche Boni im GeoReport
                  orientierend zu berücksichtigen.
                </p>
              </div>

              {form.gebaeudetyp === "Bestand (Sanierung)" ? (
                <div className="mt-5 space-y-5">
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
                    <input
                      type="checkbox"
                      name="self_occupied"
                      checked={form.self_occupied}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                    />
                    <span>
                      <span className="block font-semibold text-slate-800">
                        Ich bin Eigentümer/in und nutze die Wohneinheit selbst
                        als Haupt- oder alleinigen Wohnsitz.
                      </span>
                      <span className="mt-1 block text-sm leading-6 text-slate-500">
                        Selbstnutzung ist Voraussetzung für Einkommens- und
                        Klimageschwindigkeitsbonus.
                      </span>
                    </span>
                  </label>

                  {form.self_occupied && (
                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-700">
                          Zu versteuerndes Haushaltsjahreseinkommen (zVE)
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            step="100"
                            name="taxable_household_income"
                            placeholder="z. B. 45.000"
                            value={form.taxable_household_income ?? ""}
                            onChange={handleChange}
                            className={fieldClassName}
                          />
                        </div>
                        <p className="mt-1 text-sm leading-5 text-slate-500">
                          Optional, Angabe in Euro. Gemeint ist das zVE nach
                          KfW-Definition, nicht das Bruttoeinkommen.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
                          <input
                            type="checkbox"
                            name="has_eligible_child"
                            checked={form.has_eligible_child}
                            onChange={handleChange}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                          />
                          <span className="text-sm leading-6 text-slate-700">
                            Mindestens ein kindergeldberechtigtes Kind unter
                            18 Jahren lebt im Haushalt.
                          </span>
                        </label>

                        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
                          <input
                            type="checkbox"
                            name="speed_bonus_eligible"
                            checked={form.speed_bonus_eligible}
                            onChange={handleChange}
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                          />
                          <span>
                            <span className="block text-sm font-semibold leading-6 text-slate-700">
                              Voraussetzungen für den
                              Klimageschwindigkeitsbonus sind nach eigener
                              Einschätzung erfüllt.
                            </span>
                            <span className="mt-1 block text-sm leading-5 text-slate-500">
                              Zum Beispiel beim förderfähigen Austausch einer
                              funktionsfähigen Öl-, Kohle-, Gas-Etagen- oder
                              Nachtspeicherheizung beziehungsweise einer
                              mindestens 20 Jahre alten Gas- oder
                              Biomasseheizung.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-600">
                  Das KfW-Programm 458 gilt für bestehende Wohngebäude. Für den
                  ausgewählten Neubau werden deshalb keine persönlichen Boni
                  angesetzt.
                </div>
              )}

              <p className="mt-4 text-xs leading-5 text-slate-500">
                Die Angaben dienen ausschließlich der unverbindlichen
                Förderabschätzung. Ob alle Voraussetzungen erfüllt sind,
                entscheiden KfW und Fachunternehmen im Antragsverfahren.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Paket</label>
              <select
                name="paket"
                value={form.paket}
                onChange={handleChange}
                className={fieldClassName}
              >
				<option value="Beta">Beta (0 €)</option>
              </select>
            </div>
          </form>
		  

          {/* Hinweisfeld */}
          {(validationErrors.length > 0 || liveValidationErrors.length > 0) && (
            <div className="mt-6 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4">
              <h3 className="font-semibold mb-2">
                Bitte prüfen Sie folgende Angaben:
              </h3>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                {(validationErrors.length > 0
                  ? validationErrors
                  : liveValidationErrors
                ).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
              <p className="text-sm mt-3 text-amber-800">
                Pflichtangaben sind Wohnfläche und unbebaute
                Grundstücksgröße sowie bei Bestandsgebäuden zusätzlich das
                Baujahr. Die Förderangaben sind freiwillig.
              </p>
            </div>
          )}

          <div className="mt-4 mb-3">
            <label className="text-sm leading-relaxed cursor-pointer block">
              <input
                type="checkbox"
                checked={consentToWaiver}
                onChange={(e) => setConsentToWaiver(e.target.checked)}
                className="mr-2"
              />
              Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags
              vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass
              ich dadurch mein Widerrufsrecht verliere. Zudem habe ich die{" "}
              <Link
                to="/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 underline"
              >
                AGB
              </Link>{" "}
              sowie die{" "}
              <Link
                to="/haftung"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 underline"
              >
                Haftung & Nutzungshinweise
              </Link>{" "}
              gelesen und akzeptiere diese.
            </label>
          </div>

          {!consentToWaiver && (
            <p className="text-sm text-slate-500">
              Für den Kauf des GeoReports ist diese Zustimmung erforderlich.
            </p>
          )}
		  


		<div className="mb-2 mt-10 text-center">

		  {/* ❌ Kauf-Button nur wenn NICHT Beta */}
		  {!isBeta && (
			<>
			  <button
				onClick={handleCheckout}
				disabled={!canCheckout}
				className={`inline-block px-10 py-4 rounded-full shadow-lg font-semibold text-lg transition ${
				  canCheckout
					? "bg-red-600 hover:bg-red-700 text-white"
					: "bg-slate-300 text-slate-500 cursor-not-allowed"
				}`}
			  >
				GeoReport kaufen
			  </button>

			  {!canCheckout && (
				<p className="text-sm text-slate-500 mt-3">
				  Bitte vervollständigen Sie die Pflichtangaben und bestätigen Sie
				  die Adresse, bevor Sie fortfahren.
				</p>
			  )}
			</>
		  )}

		  {/* ✅ Beta / Dev Button */}
		  {(isBeta || isLocalhost) && (
			<div className="mt-4">
			  <button
				onClick={handleLocalPDF}
				className="w-full rounded-xl bg-red-600 px-7 py-3.5 font-semibold text-white shadow-sm transition hover:bg-red-700 md:w-auto"
			  >
				{isBeta
				  ? "GeoReport kostenlos testen (Beta)"
				  : "PDF lokal testen (nur für Entwicklung)"}
			  </button>
			</div>
		  )}

		</div>
        </div>
      </div>
    </div>
  );
}